import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import { motion } from "framer-motion";
import "./PortfolioPopup.css";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import CryptoJS from "crypto-js";

const PortfolioPopup = ({ open, onClose, onEditNow }) => {
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);
  const [once, setOnce] = useState(true);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    setOnce(false);
  };

  const accordionData = [
    {
      id: "panel1",
      title: "What exactly is this portfolio?",
      content: [
        "A fully editable tech profile",
        "Present your skills, certificates, contests, rankings and projects in one place",
        "Clean UI made for placement and hiring",
        "Works perfectly on mobile and desktop",
      ],
    },
    {
      id: "panel2",
      title: "How does this help me?",
      content: [
        "Recruiters decide in seconds",
        "This layout instantly highlights your strengths",
        "Makes you look structured and serious",
        "Students who used this saw better callback rates",
      ],
    },
    {
      id: "panel3",
      title: "What can I edit inside?",
      content: [
        "Skills",
        "Certificates",
        "Leetcode and GFG rankings",
        "GitHub contributions",
        "Projects",
        "Achievements",
        "Bio, photo and links",
        "Your own custom sub-domain",
        "Update anytime, unlimited edits",
      ],
    },
    {
      id: "panel4",
      title: "How 400+ Students got interview calls",
      content: [
        "Easy to share in applications",
        "Clean visual design built for hiring",
        "Works like a structured portfolio CV",
        "One-tap updates",
        "Helps you stand out without writing long resumes",
      ],
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: "rgba(18, 18, 18, 0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          borderRadius: "16px",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
          width: "90vw",
        },
      }}
    >
      <DialogContent sx={{ padding: "32px" }}>
        {/* Title and Subtitle */}
        <Box sx={{ marginBottom: "24px", textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              marginBottom: "8px",
              fontFamily: "Questrial",
            }}
          >
            Your Tech Portfolio
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: "16px",
              fontFamily: "Questrial",
            }}
          >
            Everything recruiters want to see, in one clean place
          </Typography>
        </Box>

        {/* Accordions */}
        <Box sx={{ marginBottom: "24px", fontFamily: "Questrial" }}>
          {accordionData.map((item, index) => (
            <Accordion
              key={item.id}
              expanded={expanded === item.id || (index === 0 && once)}
              onChange={handleChange(item.id)}
              sx={{
                background: "rgba(255, 255, 255, 0.05)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "8px !important",
                fontFamily: "Questrial",
                marginBottom: "12px",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "0 0 12px 0",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                sx={{
                  color: "#fff",
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontFamily: "Questrial" }}>
                  {item.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  paddingTop: 0,
                }}
              >
                <ul style={{ margin: 0, paddingLeft: "20px" }}>
                  {item.content.map((line, index) => (
                    <li key={index} style={{ marginBottom: "8px" }}>
                      {line}
                    </li>
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            marginTop: "24px",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              borderColor: "rgba(255, 255, 255, 0.2)",
              padding: "10px 24px",
              textTransform: "none",
              fontSize: "16px",
              fontFamily: "Questrial",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.4)",
                background: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            Close
          </Button>

          <motion.button
            className="shimmer-button"
            onClick={() =>
              history.push(
                `/techprofile/edit/${CryptoJS.lib.WordArray.random(
                  16
                ).toString()}`
              )
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              boxShadow: [
                "0 0 20px rgba(255, 215, 0, 0.5)",
                "0 0 40px rgba(255, 215, 0, 0.8)",
                "0 0 20px rgba(255, 215, 0, 0.5)",
              ],
            }}
            transition={{
              boxShadow: {
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }}
          >
            <motion.span
              className="button-content"
              animate={{
                x: [0, 0, 0, 0, 0],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <motion.span
                className="edit-icon"
                animate={{
                  rotate: [0, -10, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  repeatDelay: 1,
                }}
              >
                <EditIcon sx={{ fontSize: "20px", marginRight: "8px" }} />
              </motion.span>
              <span className="button-text">Edit Now</span>
            </motion.span>
            <span className="shimmer-overlay"></span>
          </motion.button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PortfolioPopup;
